import { atom, selector } from "recoil";

import { DiagramEdge, DiagramNode } from "../Diagrams/ConnectionCreatorDiagram/ConnectionCreatorDiagram";

const MAX_HISTORY_SIZE = 50;

export interface DiagramSnapshot {
    nodes: DiagramNode[];
    edges: DiagramEdge[];
    notes?: string;
}

export interface DiagramHistoryState {
    history: DiagramSnapshot[];
    currentIndex: number; // -1 means no history yet
    savedIndex: number; // -1 means never saved
}

export const diagramHistoryAtom = atom<DiagramHistoryState>({
    key: "diagramHistory",
    default: {
        history: [],
        currentIndex: -1,
        savedIndex: -1,
    },
});

export const isDiagramDirtySelector = selector<boolean>({
    key: "isDiagramDirty",
    get: ({ get }) => {
        const state = get(diagramHistoryAtom);
        // Dirty if we have history and current index differs from saved index
        return state.currentIndex !== state.savedIndex;
    },
});

export const canUndoSelector = selector<boolean>({
    key: "canUndoDiagram",
    get: ({ get }) => {
        const state = get(diagramHistoryAtom);
        return state.currentIndex > 0;
    },
});

export const canRedoSelector = selector<boolean>({
    key: "canRedoDiagram",
    get: ({ get }) => {
        const state = get(diagramHistoryAtom);
        return state.currentIndex < state.history.length - 1;
    },
});

export const currentDiagramStateSelector = selector<DiagramSnapshot | null>({
    key: "currentDiagramState",
    get: ({ get }) => {
        const state = get(diagramHistoryAtom);
        if (state.currentIndex < 0 || state.currentIndex >= state.history.length) {
            return null;
        }
        return state.history[state.currentIndex];
    },
});

// Helper functions for state manipulation (used by the hook)
export const pushHistoryState = (
    currentState: DiagramHistoryState,
    snapshot: DiagramSnapshot
): DiagramHistoryState => {
    // If we're not at the end of history, truncate future states
    const newHistory = currentState.history.slice(0, currentState.currentIndex + 1);

    // Add new snapshot
    newHistory.push(snapshot);

    // Trim to max size if needed
    if (newHistory.length > MAX_HISTORY_SIZE) {
        const trimCount = newHistory.length - MAX_HISTORY_SIZE;
        newHistory.splice(0, trimCount);

        // Adjust savedIndex if it was in the trimmed portion
        const newSavedIndex =
            currentState.savedIndex < trimCount ? -1 : currentState.savedIndex - trimCount;

        return {
            history: newHistory,
            currentIndex: newHistory.length - 1,
            savedIndex: newSavedIndex,
        };
    }

    return {
        history: newHistory,
        currentIndex: newHistory.length - 1,
        savedIndex: currentState.savedIndex,
    };
};

export const undoHistoryState = (currentState: DiagramHistoryState): DiagramHistoryState => {
    if (currentState.currentIndex <= 0) {
        return currentState;
    }

    return {
        ...currentState,
        currentIndex: currentState.currentIndex - 1,
    };
};

export const redoHistoryState = (currentState: DiagramHistoryState): DiagramHistoryState => {
    if (currentState.currentIndex >= currentState.history.length - 1) {
        return currentState;
    }

    return {
        ...currentState,
        currentIndex: currentState.currentIndex + 1,
    };
};

export const markAsSavedState = (currentState: DiagramHistoryState): DiagramHistoryState => {
    return {
        ...currentState,
        savedIndex: currentState.currentIndex,
    };
};

export const resetHistoryState = (): DiagramHistoryState => {
    return {
        history: [],
        currentIndex: -1,
        savedIndex: -1,
    };
};
